import { type FC, useCallback, useMemo } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import {
  F0Box,
  F0Button,
  F0Checkbox,
  F0Form,
  F0Icon,
  F0Link,
  F0Text,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import { AlertCircle } from '@factorialco/f0-react/icons/app'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import i18n from 'modules/core/lib/i18n'
import { useDeviceCatalogCartContext } from 'modules/it_management/contexts/DeviceCatalogCartContext'
import { useDeviceCatalogCheckout } from 'modules/it_management/hooks/device_catalog/useDeviceCatalogCheckout'
import { navigation } from 'modules/it_management/lib/deviceCatalogNavigation'

import { CartItemRow } from 'modules/it_management/components/device_catalog/MarketplaceCartSidepanel/CartItemRow'

const toNonEmpty = (...values: (string | null | undefined)[]) =>
  values.find((value) => value?.trim())?.trim()

const OrderPlanPage: FC = () => {
  const navigate = useNavigate()
  const { access, email } = useCurrentSession()
  const cart = useDeviceCatalogCartContext()
  const { formRef, submit, hasErrors } = useF0Form()

  const orderPlanSubmittedPath = `/device-catalog/${navigation.children.orderPlanSubmitted.path}`

  const { handleCheckout, isCheckingOut } = useDeviceCatalogCheckout(cart, {
    onSuccess: () => navigate(orderPlanSubmittedPath),
  })

  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const deviceCount = cart.itemCount

  const mandatoryFieldError = i18n.t(
    'it_management.device_catalog.order_plan.details.required_error'
  )
  const defaultCustomerName =
    toNonEmpty(access.fullName, [access.firstName, access.lastName].filter(Boolean).join(' ')) ?? ''
  const defaultEmail = toNonEmpty(email) ?? ''

  const schema = useMemo(
    () =>
      z.object({
        customerName: f0FormField(z.string().trim().optional(), {
          label: i18n.t('it_management.device_catalog.order_plan.details.full_name_label'),
          disabled: true,
        }),
        workEmail: f0FormField(z.string(), {
          label: i18n.t('it_management.device_catalog.order_plan.details.work_email_label'),
          helpText: i18n.t('it_management.device_catalog.order_plan.details.work_email_helper'),
          disabled: true,
        }),
        phoneNumber: f0FormField(z.string().trim().min(1, mandatoryFieldError), {
          label: i18n.t('it_management.device_catalog.order_plan.details.phone_number_label'),
          placeholder: '+34 600 000 000',
          helpText: i18n.t('it_management.device_catalog.order_plan.details.phone_number_helper'),
        }),
        consentUserData: f0FormField(z.string().min(1, mandatoryFieldError), {
          fieldType: 'custom',
          label: `${i18n.t('it_management.device_catalog.order_plan.details.consent_label')}`,
          render: ({ id, value, onChange, error, disabled }) => (
            <F0Box display="flex" flexDirection="column" gap="xs">
              <F0Box display="flex" alignItems="start" gap="xs">
                <F0Box
                  border={error ? 'default' : undefined}
                  borderColor={error ? 'critical' : undefined}
                  borderRadius="sm"
                  padding={error ? 'xs' : 'none'}
                >
                  <F0Checkbox
                    id={id}
                    hideLabel
                    checked={value === 'true'}
                    onCheckedChange={(checked) => onChange(checked ? 'true' : '')}
                    disabled={disabled}
                  />
                </F0Box>
                <F0Box>
                  <F0Text
                    variant="body"
                    as="span"
                    content={i18n.t(
                      'it_management.device_catalog.order_plan.details.consent_label_prefix'
                    )}
                  />{' '}
                  <F0Link
                    href="https://factorialhr.com/sub-processors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n.t('it_management.device_catalog.order_plan.details.consent_link_label')}
                  </F0Link>{' '}
                  <F0Text
                    variant="body"
                    as="span"
                    content={i18n.t(
                      'it_management.device_catalog.order_plan.details.consent_label_suffix'
                    )}
                  />
                </F0Box>
              </F0Box>
              {error && (
                <F0Box paddingTop="xs">
                  <F0Box display="flex" alignItems="center" gap="sm">
                    <F0Icon icon={AlertCircle} size="md" color="critical" />
                    <F0Text
                      variant="body"
                      as="span"
                      style={{ color: 'hsl(var(--critical-70))', fontWeight: 500 }}
                      content={error}
                    />
                  </F0Box>
                </F0Box>
              )}
            </F0Box>
          ),
        }),
      }),
    [mandatoryFieldError]
  )

  const defaultValues = useMemo(
    () => ({
      customerName: defaultCustomerName,
      workEmail: defaultEmail,
      phoneNumber: '',
      consentUserData: '',
    }),
    [defaultCustomerName, defaultEmail]
  )

  const handlePlaceOrder = useCallback(() => {
    submit()
  }, [submit])

  const handleSubmit = useCallback(
    async (values: z.infer<typeof schema>) => {
      await handleCheckout({
        customerName: values.customerName ?? defaultCustomerName,
        phoneNumber: values.phoneNumber,
        consentUserData: values.consentUserData === 'true',
      })

      return { success: true as const }
    },
    [defaultCustomerName, handleCheckout]
  )

  const formDefinition = useF0FormDefinition({
    name: 'it-management-device-catalog-order-review',
    schema,
    defaultValues,
    submitConfig: { type: 'default', hideSubmitButton: true },
    onSubmit: async ({ data }) => handleSubmit(data),
  })

  if (cart.items.length === 0) {
    return <Navigate to="/device-catalog" replace />
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading}
    >
      <F0Box width="full" display="flex" flexDirection="column">
        <F0Box width="full" borderBottom="default" borderColor="secondary">
          <ResourceHeader
            title={i18n.t('it_management.device_catalog.order_plan.title')}
            description={i18n.t('it_management.device_catalog.order_plan.subtitle', {
              count: deviceCount,
            })}
          />
        </F0Box>

        <F0Box display="flex" justifyContent="center">
          <F0Box maxWidth="screen-md" display="flex" flexDirection="column">
            <F0Box display="flex" flexDirection="column" paddingX="xl" paddingY="lg" gap="lg">
              <F0Box display="flex" flexDirection="column" gap="md">
                <F0Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="between"
                  alignItems="center"
                >
                  <F0Text
                    variant="label"
                    content={i18n.t(
                      'it_management.device_catalog.order_plan.selected_devices_title'
                    )}
                  />
                  <F0Text
                    variant="description"
                    content={i18n.t(
                      'it_management.device_catalog.order_plan.selected_devices_count',
                      {
                        count: deviceCount,
                      }
                    )}
                  />
                </F0Box>

                <F0Box
                  borderTop="default"
                  borderBottom="default"
                  borderColor="secondary"
                  paddingY="md"
                >
                  {cart.items.map((item) => (
                    <CartItemRow
                      key={`${item.variantId}-${item.offerId}`}
                      item={item}
                      onUpdateQuantity={cart.updateQuantity}
                      onRemove={cart.removeItem}
                      variant="checkout"
                    />
                  ))}
                </F0Box>

                <F0Box>
                  <F0Text
                    variant="small"
                    content={i18n.t(
                      'it_management.device_catalog.order_plan.selected_devices_note'
                    )}
                  />
                </F0Box>
              </F0Box>

              <F0Box display="flex" flexDirection="column" gap="md">
                <F0Box display="flex" flexDirection="column" gap="xs">
                  <F0Text
                    variant="label"
                    content={i18n.t('it_management.device_catalog.order_plan.details.title')}
                  />
                  <F0Text
                    variant="description"
                    content={i18n.t('it_management.device_catalog.order_plan.details.subtitle')}
                  />
                </F0Box>

                <F0Box marginTop="md" paddingBottom="sm">
                  <F0Form formRef={formRef} formDefinition={formDefinition} />
                </F0Box>
              </F0Box>

              <F0Box
                display="flex"
                flexDirection="row"
                justifyContent="between"
                alignItems="center"
                gap="md"
              >
                <F0Text
                  variant="small"
                  content={i18n.t('it_management.device_catalog.order_plan.footer_summary', {
                    count: deviceCount,
                  })}
                />
                <F0Button
                  label={i18n.t('it_management.device_catalog.order_plan.request_information')}
                  variant="default"
                  size="md"
                  onClick={handlePlaceOrder}
                  disabled={isCheckingOut || hasErrors}
                />
              </F0Box>
            </F0Box>
          </F0Box>
        </F0Box>
      </F0Box>
    </ResponsivePage.Body>
  )
}

export default OrderPlanPage
